// =====================================================================
// CLINIC ADMIN API — axios instance separada pra falar com o workflow
// "Agenda Admin CRUD v2" no n8n. NAO usa o `api` (backend CRM) — o n8n
// vive em outro dominio (n8n.clinicaclee.com).
//
// Token short-lived (15min): pedimos pro backend CRM (que sabe o
// jwt_secret por-tenant em clinic_config), cacheamos por slug, e
// renovamos antes de expirar (janela de seguranca 60s).
//
// Cada chamada DEVE incluir `slug` em params (ou data, ou header
// X-Clinic-Slug). O interceptor pega o token correto pra aquele tenant.
//
// Fase 3 white-label (S38).
// =====================================================================
import axios from "axios";
import api from "./api";

const N8N_BASE_URL =
  process.env.REACT_APP_N8N_URL ||
  "https://n8n.genius.med.br/webhook/clinic-api";

// Cache: { [slug]: { token, expiresAt (ms epoch) } }
const tokenCache = {};
const SAFETY_WINDOW_MS = 60 * 1000; // renova 60s antes de expirar
const inFlight = {}; // dedup: evita N requests simultaneos pro mesmo slug

// Busca/renova o token pra um slug. Throws se sem permissao.
export const ensureAdminToken = async (slug) => {
  if (!slug) throw new Error("slug requerido pra ensureAdminToken");

  const cached = tokenCache[slug];
  const now = Date.now();
  if (cached && cached.expiresAt - SAFETY_WINDOW_MS > now) {
    return cached.token;
  }

  // Dedup: se ja temos request voando pro mesmo slug, espera ela.
  if (inFlight[slug]) {
    return inFlight[slug];
  }

  const p = (async () => {
    try {
      const { data } = await api.get("/clinic-admin/token", {
        params: { slug }
      });
      if (!data || !data.token) {
        throw new Error("backend nao retornou token clinic-admin");
      }
      tokenCache[slug] = {
        token: data.token,
        expiresAt: now + data.expiresIn * 1000
      };
      return data.token;
    } finally {
      delete inFlight[slug];
    }
  })();

  inFlight[slug] = p;
  return p;
};

// Limpa cache. Sem args = limpa tudo (ex: logout).
export const clearAdminTokenCache = (slug) => {
  if (slug) {
    delete tokenCache[slug];
  } else {
    Object.keys(tokenCache).forEach((k) => delete tokenCache[k]);
  }
};

// axios pro n8n. Bearer token injetado por chamada via interceptor.
const n8nApi = axios.create({
  baseURL: N8N_BASE_URL,
  timeout: 30000
});

// Helper: extrai slug de varias fontes possiveis.
const pickSlug = (config) => {
  if (!config) return null;
  if (config.params && config.params.slug) return config.params.slug;
  if (config.data && typeof config.data === "object" && config.data.slug) {
    return config.data.slug;
  }
  if (config.headers && config.headers["X-Clinic-Slug"]) {
    return config.headers["X-Clinic-Slug"];
  }
  return null;
};

// REQUEST: pega token (renova se preciso) e injeta como Bearer.
n8nApi.interceptors.request.use(async (config) => {
  const slug = pickSlug(config);
  if (!slug) {
    return Promise.reject(
      new Error(
        "Chamada n8n sem slug — passe em params, data ou header X-Clinic-Slug"
      )
    );
  }
  const token = await ensureAdminToken(slug);
  config.headers = config.headers || {};
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// RESPONSE: se 401, invalida o token cacheado (proxima chamada pega novo).
n8nApi.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err && err.response && err.response.status === 401) {
      const slug = pickSlug(err.config);
      if (slug) clearAdminTokenCache(slug);
    }
    return Promise.reject(err);
  }
);

export default n8nApi;
